import { EditButton } from "@/components/admin/edit-button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Mail, Phone, ShieldCheck, User } from "lucide-react";
import { ShowBase, useGetIdentity, useGetList, useShowContext } from "ra-core";
import type { Sale, UserRole } from "../types";

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  super_admin: { label: "Super Admin", color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
  sales_manager: { label: "Sales Manager", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  sales_rep: { label: "Sales Rep", color: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200" },
  finance_manager: { label: "Finance Manager", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  procurement_officer: { label: "Procurement Officer", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" },
  designer: { label: "Designer", color: "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200" },
};

function getUserInitials(record: Sale) {
  return `${record.first_name?.[0] ?? ""}${record.last_name?.[0] ?? ""}`.toUpperCase();
}

export function SalesShow() {
  return (
    <ShowBase>
      <SalesShowContent />
    </ShowBase>
  );
}

function SalesShowContent() {
  const { record, isPending } = useShowContext<Sale>();
  const { identity } = useGetIdentity();
  const { data: roles } = useGetList("roles", { pagination: { page: 1, perPage: 50 }, sort: { field: "name", order: "ASC" } });

  if (isPending || !record) return null;

  const roleName = roles?.find((r: UserRole) => r.slug === record?.role)?.name;
  const isAdmin = roleName === "Super Admin" || record?.role === "super_admin";
  
  const roleInfo = record.role ? ROLE_LABELS[record.role] : null;
  const isSelf = record.id === identity?.id;

  return (
    <div className="max-w-3xl w-full mx-auto mt-2 space-y-4">
      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            {/* Avatar */}
            <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-xl font-bold text-primary">
              {record.avatar?.src
                ? <img src={record.avatar.src} alt={record.first_name} className="w-16 h-16 rounded-full object-cover" />
                : getUserInitials(record)
              }
            </div>

            {/* Name + Badges */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-foreground">
                  {record.first_name} {record.last_name}
                </h2>
                {isSelf && (
                  <Badge variant="outline" className="text-xs border-primary text-primary">
                    You
                  </Badge>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-2.5">
                {roleInfo && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${roleInfo.color}`}>
                    <ShieldCheck size={11} />
                    {roleInfo.label}
                  </span>
                )}
                {record.department && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    <Building2 size={11} />
                    {record.department}
                  </span>
                )}
                {isAdmin && (
                  <Badge variant="outline" className="border-blue-400 text-blue-600 text-xs">
                    Admin
                  </Badge>
                )}
                {record.disabled && (
                  <Badge variant="outline" className="border-orange-400 text-orange-600 text-xs">
                    Disabled
                  </Badge>
                )}
              </div>
            </div>

            <div className="shrink-0 self-start sm:self-center">
              <EditButton />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Details */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <User size={14} /> Contact Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 text-sm">
              <Mail size={16} className="text-muted-foreground flex-shrink-0" />
              <span className="text-foreground">{record.email}</span>
            </div>
            {record.phone && (
              <div className="flex items-center gap-3 text-sm">
                <Phone size={16} className="text-muted-foreground flex-shrink-0" />
                <span className="text-foreground">{record.phone}</span>
              </div>
            )}
            {record.department && (
              <div className="flex items-center gap-3 text-sm">
                <Building2 size={16} className="text-muted-foreground flex-shrink-0" />
                <span className="text-foreground">{record.department}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
